/**
 * Binding-key resolution for `editor_bindings`.
 *
 * A renderer marks every repeated element it draws — `data.cards.0.title`,
 * `data.cards.1.title`, `data.cards.2.title` — but a binding map that
 * enumerated every index would be unbounded. Binding keys therefore use a
 * `*` segment to say "this array generalizes": `data.cards.*.title` covers
 * every card.
 *
 * Resolution is exact-first, then one generalized lookup. `*` appears ONLY
 * in binding keys, never in an incoming field path, so a key that keeps a
 * literal index stays literal: `data.slides.0.title` matches index 0 and
 * nothing else. That is deliberate for carousel slides — one slide is on
 * screen at a time, so inline-editing slide 3 has no on-canvas meaning —
 * and it is why generalization is opt-in per key rather than global.
 *
 * INLINED at `apps/portal/api/_lib/contentPageFieldPatch.ts` (Vercel bundles
 * `api/**` per function and cannot resolve workspace packages). Parity is
 * guarded by `apps/portal/tests/unit/bindingPathInline.test.ts` — keep the
 * two in sync on every edit.
 */

const INDEX_SEGMENT = /^(0|[1-9][0-9]*)$/;

/**
 * Resolve a concrete field path to the binding key that governs it, or
 * `undefined` when no key does.
 *
 * Membership is checked with `hasOwnProperty` so an inherited `Object`
 * property (`toString`, `constructor`) can never resolve to a binding.
 */
export function resolveBindingKey(
  fieldPath: string,
  bindings: Readonly<Record<string, unknown>>,
): string | undefined {
  if (Object.prototype.hasOwnProperty.call(bindings, fieldPath)) return fieldPath;
  const segments = fieldPath.split('.');
  let generalized = false;
  for (let i = 0; i < segments.length; i++) {
    if (INDEX_SEGMENT.test(segments[i])) {
      segments[i] = '*';
      generalized = true;
    }
  }
  if (!generalized) return undefined;
  const key = segments.join('.');
  return Object.prototype.hasOwnProperty.call(bindings, key) ? key : undefined;
}
